import { Icons } from '@/libs/components/src/lib/icons';
import { Button } from '@chiroup/components/Button';
import {
  ClinicPatientReindexRequest,
  ClinicPatientReindexResponse,
} from '@chiroup/core/types/Devtools.type';
import { FormEvent, useRef, useState } from 'react';
import useDevtools from '../../../hooks/useDevtools';

const BATCH_SIZE = 250;

type PatientPanelDevtools = {
  reindexPatients: (request: ClinicPatientReindexRequest) => Promise<ClinicPatientReindexResponse>;
};

type ReindexProgress = {
  totalPatients: number;
  processed: number;
  indexed: number;
  previousRoutingProcessed: number;
  failed: number;
  indexedPatientCount?: number;
  countVerified?: boolean;
};

const ClinicPatientReindex = () => {
  const { reindexPatients } = useDevtools('patient-panel') as PatientPanelDevtools;
  const [clinicId, setClinicId] = useState('');
  const [previousClinicId, setPreviousClinicId] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [wasStopped, setWasStopped] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState<ReindexProgress | null>(null);
  const cancelRef = useRef(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsedClinicId = Number(clinicId);
    const parsedPreviousClinicId = previousClinicId ? Number(previousClinicId) : undefined;

    if (!Number.isInteger(parsedClinicId) || parsedClinicId <= 0) {
      setError('Enter a valid destination clinic ID.');
      return;
    }

    if (
      parsedPreviousClinicId !== undefined &&
      (!Number.isInteger(parsedPreviousClinicId) || parsedPreviousClinicId <= 0)
    ) {
      setError('Enter a valid previous clinic ID.');
      return;
    }

    cancelRef.current = false;
    setError('');
    setWasStopped(false);
    setIsProcessing(true);
    setProgress(null);

    let startAfterId: string | undefined;
    let totals: ReindexProgress = {
      totalPatients: 0,
      processed: 0,
      indexed: 0,
      previousRoutingProcessed: 0,
      failed: 0,
    };

    try {
      do {
        const result = await reindexPatients({
          clinicId: parsedClinicId,
          previousClinicId: parsedPreviousClinicId,
          startAfterId,
          batchSize: BATCH_SIZE,
        });

        totals = {
          totalPatients: result.totalPatients,
          processed: totals.processed + result.processed,
          indexed: totals.indexed + result.indexed,
          previousRoutingProcessed:
            totals.previousRoutingProcessed + result.previousRoutingProcessed,
          failed: totals.failed + result.failed,
          indexedPatientCount: result.indexedPatientCount,
          countVerified: result.countVerified,
        };
        setProgress(totals);

        if (result.failed > 0) {
          setError(
            result.hasMore
              ? 'A search operation failed. The run stopped before the next batch.'
              : 'The final batch had failed search operations.'
          );
          break;
        }

        startAfterId = result.nextStartAfterId;

        if (!result.hasMore) break;
      } while (!cancelRef.current);

      if (cancelRef.current) {
        setWasStopped(true);
      }
    } catch (caught) {
      const responseMessage = (caught as { response?: { data?: { message?: string } } })?.response
        ?.data?.message;
      setError(
        responseMessage || (caught instanceof Error ? caught.message : 'Patient reindexing failed.')
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const isVerified =
    progress?.countVerified === true &&
    progress.failed === 0 &&
    progress.processed === progress.totalPatients;

  return (
    <form onSubmit={handleSubmit}>
      <section className="bg-white border border-gray-400 rounded-lg">
        <div className="p-4 flex flex-row justify-between items-center gap-4">
          <div>
            <h1 className="font-extrabold">Reindex Clinic Patients</h1>
            <p className="mt-1 text-sm text-gray-600">
              Rebuilds every patient search document from the clinic&apos;s current MySQL data.
            </p>
          </div>
          {isProcessing ? (
            <Button
              text="Stop"
              onClick={() => {
                cancelRef.current = true;
              }}
              type="button"
              icon={Icons.pause}
            />
          ) : (
            <Button text="Reindex clinic" type="submit" icon={Icons.puzzle} disabled={!clinicId} />
          )}
        </div>

        <div className="p-4 m-4 border border-gray-400 bg-gray-50 rounded-lg text-sm space-y-2">
          <p>
            The optional previous clinic ID removes these same patients from their old search
            routing after each destination document is indexed successfully.
          </p>
          <p>
            The final batch verifies that the destination clinic&apos;s OpenSearch count matches its
            MySQL patient count.
          </p>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <label htmlFor="reindex-clinic-id" className="block text-sm font-medium text-gray-900">
            Destination clinic ID
            <input
              id="reindex-clinic-id"
              name="clinicId"
              type="number"
              min={1}
              required
              value={clinicId}
              onChange={event => setClinicId(event.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
          <label
            htmlFor="reindex-previous-clinic-id"
            className="block text-sm font-medium text-gray-900"
          >
            Previous clinic ID (optional)
            <input
              id="reindex-previous-clinic-id"
              name="previousClinicId"
              type="number"
              min={1}
              value={previousClinicId}
              onChange={event => setPreviousClinicId(event.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
        </div>

        {(progress || error || wasStopped) && (
          <div className="p-4 border-t border-gray-300 text-sm space-y-1" aria-live="polite">
            {progress && (
              <>
                <p>
                  Processed {progress.processed} of {progress.totalPatients}; indexed{' '}
                  {progress.indexed}; previous routing processed {progress.previousRoutingProcessed}
                  ; failed {progress.failed}.
                </p>
                {progress.indexedPatientCount !== undefined && (
                  <p>
                    Search count: {progress.indexedPatientCount}. MySQL count:{' '}
                    {progress.totalPatients}.
                  </p>
                )}
              </>
            )}
            {isVerified && (
              <p className="font-semibold text-green-700">
                Complete. All patients were indexed and the counts match.
              </p>
            )}
            {progress?.countVerified === false && (
              <p className="font-semibold text-red-700">
                Count mismatch. The reindex is not verified.
              </p>
            )}
            {wasStopped && (
              <p className="font-semibold text-amber-700">Stopped after this batch.</p>
            )}
            {error && <p className="font-semibold text-red-700">{error}</p>}
          </div>
        )}
      </section>
    </form>
  );
};

export default ClinicPatientReindex;
